<template>
  <fieldset class="toggle-options"
            :class="{'toggle-options--left': this.leftSelected,
            'toggle-options--right': this.rightSelected,
            'toggle-options--disabled': this.disabled}"
            :style="{'--width': width}"
  >
    <label v-if="label" class="toggle-options-label">{{label}}</label>
    <div @click.prevent="toggle" class="toggle-options-switch" :style="toggleStyles">
      <div v-for="(option, i) in options" :key="option.value">
        <input :disabled="disabled" class="toggle-options-switch__input" v-model="model" type="radio" :id="option.value" :name="id" :value="option.value" >
        <label class="toggle-options-switch__text" :class="[`option-${i}`]" :style="option === currentSelected ? activeTextStyles : {}" :for="option.value">{{option.label}}</label>
      </div>
      <div class="toggle-options-switch__track">
        <span class="toggle-options-switch__handle" :style="handleStyles"></span>
      </div>
    </div>
  </fieldset>
</template>

<script>
import { v4 as uuid } from 'uuid'

// Provides a toggle between two different items.
// Despite options being an array, this component only supports 2 options (right now)
// This is pretty bespoke-made for the text colour white -> black toggle, so it only really works with two options of the same text length.
// Would be great to have it automatically calculate widths & stuff in the future
export default {
  name: "ToggleOptions",

  props: {
    options: Array, // list of {value: 'blah', label: 'blah}
    value: String,
    label: String,
    offText: String,
    onText: String,
    disabled: Boolean,
    width: {
      type: String,
      default: "125px"
    },
    changeHandler: Function,
    background: String,
    scale: String,
  },

  mounted() {
    this.id = uuid()
  },

  data() {
    return {
      id: ""
    }
  },

  methods: {
    toggle() {
      if (this.disabled) return

      if (this.leftSelected) {
        this.model = this.options[1].value
      } else {
        this.model = this.options[0].value
      }
    }
  },

  computed: {
    model: {
      get() {
        return this.value
      },

      set(value) {
        this.$emit('input', value)
        if(this.changeHandler) this.changeHandler([value])
      }
    },
    currentSelected() {
      return this.options.filter(o => o.value === this.model)[0]
    },

    leftSelected() {
      return this.model == this.options[0].value
    },

    rightSelected() {
      return this.model == this.options[1].value
    },
    toggleStyles() {
      let styles = {}
      if(this.background) styles.background = this.background
      if(this.scale) styles.transform = `scale(${this.scale})`
      return styles
    },
    activeTextStyles() {
      let styles = {}
      if(this.currentSelected?.textColour) styles.color = `${this.currentSelected.textColour} !important`
      return styles
    },
    handleStyles() {
      let styles = {}
      if(this.currentSelected?.background) styles.background = this.currentSelected.background
      if(this.currentSelected?.borderColour) styles['border-color'] = `${this.currentSelected.borderColour} !important`
      return styles
    },
  }
}
</script>

<style lang="scss" scoped>
// Reset fieldset styles
legend {
  padding: 0;
  display: table;
}
fieldset {
  border: 0;
  padding: 0.01em 0 0 0;
  margin: 0;
  min-width: 0;
}
body:not(:-moz-handler-blocked) fieldset {
  display: table-cell;
}

.toggle-options {
  --height: 30px;
  --width: 90px;
  $transition: 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 10px;
  box-sizing: border-box;
  width: 225px;

  &-label {
    font-size: 0.875rem; /** 14px */
    color: white;
    font-weight: bold;
    width: 50%;
  }

  &-switch {
    cursor: pointer;
    flex-shrink: 0;
    box-sizing: border-box;
    background-color: $grey-7;
    position: relative;
    display: inline-block;
    border-radius: 15px;
    height: var(--height);
    width: var(--width);
    transform-origin: left;

    &__input {
      opacity: 0;
      position: absolute;
    }

    &__track {
      position: absolute;
      top: 2px;
      left: 2px;
      width: calc(100% - 4px);
      height: calc(100% - 4px);
    }

    &__handle {
      height: calc(var(--height) - 6px);
      width: 50%;
      border-radius: 15px;
      display: inline-block;
      background-color: none;
      border: 1px solid white;
      position: absolute;
      transition: all $transition;

      transform: translateX(0);
      left: 0;
    }

    &__text {
      pointer-events: none;
      z-index: 1;
      font-size: 0.75rem; /** 12px */
      color: $grey-3;
      text-transform: uppercase;
      font-weight: bold;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);

      opacity: 1;
      transition: all $transition;
      width: 50%;
      text-align: center;

      &.option-0 {
        left: 3px;
        color: white;
        font-weight: 800;
      }
      &.option-1 {
        right: 1px;
      }
    }
  }

  &--right {
    .toggle-options-switch {
      &__handle {
        left: 100%;
        transform: translateX(-100%);

        background-color: white;
      }

      &__text {
        &.option-0 {
          color: $grey-3;
          font-weight: bold;
        }

        &.option-1 {
          color: black;
          font-weight: 800;
        }
      }
    }
  }

  &--disabled {
    opacity: 0.5;

    .toggle-options-switch {
      cursor: unset;
    }
  }
}
</style>
